const defaultFmt = (n) => Number(n || 0).toFixed(2);

/**
 * Contenu des panneaux de détail KPI (Analytics commerçant).
 * Module .js pour tests Node sans transpile JSX.
 * Wording commerçant — formules DATA inchangées.
 */
export function buildMerchantKpiDetails({
  kpi,
  gift,
  trafic,
  commandesParStatut,
  fmtEuro = defaultFmt,
}) {
  const caNet = kpi?.ca?.value ?? 0;
  const caBrut = kpi?.ca?.brut ?? caNet;
  const caRemb = kpi?.ca?.rembourse ?? 0;
  const volume = kpi?.volume?.value ?? kpi?.commandes?.value ?? 0;
  const panier = kpi?.panierMoyen?.value ?? 0;
  const asOfLabel = gift?.asOf
    ? new Date(gift.asOf).toLocaleDateString('fr-FR')
    : 'fin de période';

  return {
    ca: {
      title: 'CA net ventes',
      summary:
        'Montant des ventes payées sur la période, après déduction des remboursements. Les ventes de cartes cadeaux ne sont pas incluses ici.',
      rows: [
        {
          label: 'Chiffre d’affaires encaissé avant remboursements',
          value: `${fmtEuro(caBrut)} €`,
        },
        { label: 'Remboursements', value: `${fmtEuro(caRemb)} €` },
        {
          label: 'Chiffre d’affaires après remboursements',
          value: `${fmtEuro(caNet)} €`,
          hint: 'Montant encaissé moins les remboursements (jamais négatif).',
        },
      ],
      method: [
        {
          label: 'Chiffre d’affaires encaissé avant remboursements :',
          value: `${fmtEuro(caBrut)} €`,
        },
        { label: '− Remboursements :', value: `${fmtEuro(caRemb)} €` },
        {
          label: '= Chiffre d’affaires net :',
          value: `${fmtEuro(caNet)} €`,
        },
      ],
      // Conservé pour tests de parité formule (non affiché si method présent)
      formula: 'CA brut − remboursements',
      listTitle: 'Évolution',
      list: [],
      comingSoon: 'Relances ciblées sur produits en baisse',
    },
    commandes: {
      title: 'Commandes payées',
      summary:
        'Nombre de commandes ayant été payées sur la période, y compris celles remboursées ensuite. Les ventes de cartes cadeaux ne sont pas comptées comme des ventes.',
      rows: [
        { label: 'Commandes payées', value: String(volume) },
        {
          label: 'Variation',
          value:
            kpi?.volume?.deltaPct != null ? `${kpi.volume.deltaPct}% vs préc.` : '—',
        },
      ],
      listTitle: 'Répartition par statut (aperçu)',
      list: (commandesParStatut || []).map((s) => ({
        key: s.statut,
        label: s.statut,
        value: String(s.count ?? s._count?.statut ?? 0),
      })),
      comingSoon: 'Relance paniers abandonnés',
    },
    panier: {
      title: 'Panier moyen ventes',
      summary:
        'Panier moyen = chiffre d’affaires net des ventes divisé par le nombre de commandes payées sur la même période.',
      rows: [
        {
          label: 'CA net pris en compte',
          value: `${fmtEuro(caNet)} €`,
        },
        {
          label: 'Commandes payées prises en compte',
          value: String(volume),
        },
        {
          label: 'Panier moyen obtenu',
          value: `${fmtEuro(panier)} €`,
        },
      ],
      method: [
        { label: 'CA net des ventes :', value: `${fmtEuro(caNet)} €` },
        { label: '÷ Commandes payées :', value: String(volume) },
        { label: '= Panier moyen :', value: `${fmtEuro(panier)} €` },
      ],
      formula: 'CA net ventes / commandes payées',
      comingSoon: 'Suggestions d’upsell automatiques',
    },
    remboursements: {
      title: 'Remboursements',
      summary:
        'Total des remboursements effectués sur les ventes de la période. Ils sont déduits du chiffre d’affaires net.',
      rows: [
        { label: 'Montant remboursé', value: `${fmtEuro(caRemb)} €` },
        {
          label: 'Chiffre d’affaires avant remboursements',
          value: `${fmtEuro(caBrut)} €`,
        },
        {
          label: 'Chiffre d’affaires après remboursements',
          value: `${fmtEuro(caNet)} €`,
        },
      ],
      method: [
        {
          label: 'Chiffre d’affaires avant remboursements :',
          value: `${fmtEuro(caBrut)} €`,
        },
        { label: '− Remboursements :', value: `${fmtEuro(caRemb)} €` },
        {
          label: '= Chiffre d’affaires net :',
          value: `${fmtEuro(caNet)} €`,
        },
      ],
      formula: 'CA brut − remboursements',
      comingSoon: 'Analyse des causes de remboursement',
    },
    trafic: {
      title: 'Trafic site boutique',
      summary: 'Pages consultées et passages sur votre boutique en ligne pendant la période.',
      rows: [
        { label: 'Pages vues', value: String(trafic?.totalVues ?? 0) },
        { label: 'Passages (sessions)', value: String(trafic?.sessionsUniques ?? 0) },
        { label: 'Pages / passage', value: String(trafic?.vuesParVisite ?? 0) },
        {
          label: 'Conversion visite → achat',
          value:
            trafic?.conversionVisiteCommande != null
              ? `${trafic.conversionVisiteCommande}%`
              : '—',
          hint: 'Part des passages ayant abouti à un achat en ligne.',
        },
      ],
      comingSoon: 'Campagnes de relance visiteurs non convertis',
    },
    gift: {
      title: 'Cartes cadeaux',
      summary:
        'Activité pendant la période (vendu / utilisé) séparée de la situation à la date de fin (encours). Les ventes de cartes cadeaux ne comptent pas dans le CA ventes.',
      rows: [
        {
          label: 'Cartes vendues (période)',
          value: `${gift?.soldCount ?? 0} · ${fmtEuro(gift?.soldAmount ?? gift?.soldNetAmount)} €`,
        },
        { label: 'Montant utilisé (période)', value: `${fmtEuro(gift?.usedAmount)} €` },
        {
          label: 'Remboursé / annulé (période)',
          value: `${fmtEuro(gift?.refundedAmount)} €`,
        },
        {
          label: `Encours disponible au ${asOfLabel}`,
          value: `${fmtEuro(gift?.outstandingAmount)} € · ${gift?.activeCount ?? 0} actives`,
          hint: 'Solde encore disponible sur les cartes à la date de fin de période.',
        },
        {
          label: 'Taux d’utilisation des cartes vendues sur la période',
          value:
            gift?.utilizationRate != null
              ? `${Math.round(gift.utilizationRate * 100)} %`
              : '—',
          hint:
            'Part du montant des cartes vendues sur la période qui a déjà été utilisée jusqu’à la fin de période.',
        },
        {
          label: 'Expirent dans les 30 jours',
          value: `${gift?.expiring30d?.count ?? 0} · ${fmtEuro(gift?.expiring30d?.amount)} €`,
        },
        {
          label: 'Expirent dans les 7 jours',
          value: `${gift?.expiring7d?.count ?? 0} · ${fmtEuro(gift?.expiring7d?.amount)} €`,
        },
        {
          label: 'Expirées avec solde',
          value: `${gift?.expiredWithBalance?.count ?? 0} · ${fmtEuro(gift?.expiredWithBalance?.amount)} €`,
        },
      ],
      comingSoon: 'Relance détenteurs avant expiration',
    },
  };
}
